package fyi.moko.hotpl.data.network

import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.GeohashEncoder
import fyi.moko.hotpl.domain.model.haversineMeters
import fyi.moko.hotpl.domain.model.SearchPrediction
import fyi.moko.hotpl.domain.model.YellowPageEntry
import fyi.moko.hotpl.domain.service.PlaceSearchException
import fyi.moko.hotpl.domain.service.PlaceSearchProvider
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

/**
 * Ktor-backed implementation of [PlaceSearchProvider] that calls the Places API (New)
 * REST endpoints (`places.googleapis.com/v1`). The legacy `maps.googleapis.com` Places
 * endpoints are disabled for this project's key and cannot be re-enabled. Designed for
 * KMP commonMain; each target must supply a platform-specific Ktor engine.
 *
 * @param client Configured [HttpClient] with JSON content negotiation. The provider
 *   installs no plugins itself.
 * @param apiKey Google Cloud API key with Places API (New) enabled.
 */
class KtorPlaceSearchProvider(
    private val client: HttpClient,
    private val apiKey: String,
) : PlaceSearchProvider {

    /** Places API (New)에 `searchNearby`가 있어 쿼리 없는 탐색이 가능하다. */
    override val supportsNearbyBrowse: Boolean = true

    override suspend fun searchAutocomplete(
        query: String,
        origin: GeoPoint?,
    ): List<SearchPrediction> {
        if (query.isBlank()) return emptyList()

        val originLatLng = origin?.let { LatLng(it.latitude, it.longitude) }
        // Bias circle centred on the user, not on a geohash cell. The radius is a
        // compromise: tight enough that a chain with branches everywhere ("starbucks")
        // returns local ones in the handful of suggestions the API sends back, wide enough
        // not to hide a place the user is naming from the next neighbourhood over.
        val locationBias = originLatLng?.let {
            LocationBias(circle = Circle(center = it, radius = SEARCH_BIAS_RADIUS_METERS))
        }

        val httpResponse = client.post("https://places.googleapis.com/v1/places:autocomplete") {
            header("X-Goog-Api-Key", apiKey)
            contentType(ContentType.Application.Json)
            setBody(
                AutocompleteRequest(
                    input = query.trim(),
                    includedPrimaryTypes = listOf("establishment"),
                    locationBias = locationBias,
                    origin = originLatLng,
                ),
            )
        }
        val response: AutocompleteResponse = httpResponse.requireOk("autocomplete").body()

        return response.suggestions.mapNotNull { suggestion ->
            val prediction = suggestion.placePrediction ?: return@mapNotNull null
            SearchPrediction(
                placeId = prediction.placeId,
                primaryText = prediction.structuredFormat.mainText.text,
                secondaryText = prediction.structuredFormat.secondaryText?.text.orEmpty(),
                distanceMeters = prediction.distanceMeters,
            )
        }
    }

    override suspend fun searchNearest(
        query: String,
        origin: GeoPoint,
        category: PlaceCategory?,
    ): List<SearchPrediction> {
        val center = LatLng(origin.latitude, origin.longitude)
        val trimmed = query.trim()
        val httpResponse = if (trimmed.isBlank()) {
            // Query-less browse: a hard restriction (not a bias) keeps it to walking range,
            // and the type list is the food/drink slice the journal is actually about.
            client.post("https://places.googleapis.com/v1/places:searchNearby") {
                header("X-Goog-Api-Key", apiKey)
                header("X-Goog-FieldMask", SEARCH_FIELD_MASK)
                contentType(ContentType.Application.Json)
                setBody(
                    SearchNearbyRequest(
                        locationRestriction = LocationRestriction(
                            circle = Circle(center = center, radius = NEARBY_BROWSE_RADIUS_METERS),
                        ),
                        includedTypes = googleNearbyTypes(category),
                    ),
                )
            }.requireOk("nearby search")
        } else {
            client.post("https://places.googleapis.com/v1/places:searchText") {
                header("X-Goog-Api-Key", apiKey)
                header("X-Goog-FieldMask", SEARCH_FIELD_MASK)
                contentType(ContentType.Application.Json)
                setBody(
                    SearchTextRequest(
                        textQuery = trimmed,
                        locationBias = LocationBias(
                            circle = Circle(center = center, radius = NEAREST_SEARCH_RADIUS_METERS),
                        ),
                    ),
                )
            }.requireOk("text search")
        }
        val response: PlacesSearchResponse = httpResponse.body()

        // These endpoints return coordinates but no distance field, so it is measured here —
        // the same figure the rows display and the list sorts on.
        return response.places.map { place ->
            SearchPrediction(
                placeId = place.id,
                primaryText = place.displayName.text,
                secondaryText = place.formattedAddress.orEmpty(),
                distanceMeters = haversineMeters(
                    origin,
                    GeoPoint(place.location.latitude, place.location.longitude),
                ).toInt(),
            )
        }
    }

    override suspend fun fetchPlaceDetails(placeId: String): YellowPageEntry {
        val httpResponse = client.get("https://places.googleapis.com/v1/places/$placeId") {
            header("X-Goog-Api-Key", apiKey)
            // Details is a GET, so the language rides in the query string rather than a body.
            parameter("languageCode", SEARCH_LANGUAGE_CODE)
            header(
                "X-Goog-FieldMask",
                "displayName,location,nationalPhoneNumber,websiteUri,types,primaryType",
            )
        }
        val result: PlaceDetailsResponse = httpResponse.requireOk("place details").body()

        val name = result.displayName.text
        val normalizedName = YellowPageEntry.normalizeName(name)
        val lat = result.location.latitude
        val lng = result.location.longitude
        val geohash = GeohashEncoder.encode(lat, lng, YellowPageEntry.GEOHASH_PRECISION)
        val regionKey = geohash.take(YellowPageEntry.REGION_KEY_LENGTH)

        return YellowPageEntry(
            entryId = YellowPageEntry.canonicalId(geohash, normalizedName),
            canonicalId = placeId,
            name = name,
            normalizedName = normalizedName,
            geohash = geohash,
            regionKey = regionKey,
            latitude = lat,
            longitude = lng,
            tags = googleTypeTags(result.types, result.primaryType),
            phoneNumber = result.nationalPhoneNumber,
            website = result.websiteUri,
        )
    }

    private suspend fun HttpResponse.requireOk(operation: String): HttpResponse {
        if (!status.isSuccess()) {
            throw PlaceSearchException(
                "Google Places $operation failed: HTTP ${status.value} ${bodyAsText()}",
            )
        }
        return this
    }

    /**
     * Returns a bias radius in meters roughly matching the precision of [geohash].
     * Geohash character length ~ precision: 4 ≈ 20km, 5 ≈ 5km, 6 ≈ 1km, 9 ≈ 5m.
     * Capped at 50km — the Places API (New) circle maximum.
     */
    private companion object {
        /** Metres; the API rejects anything above 50,000. */
        const val SEARCH_BIAS_RADIUS_METERS = 10_000.0

        /** Wider than the autocomplete bias: the user named something and may mean the next town. */
        const val NEAREST_SEARCH_RADIUS_METERS = 15_000.0

        /** "Around me" should mean around me — roughly a walk, not a drive. */
        const val NEARBY_BROWSE_RADIUS_METERS = 2_000.0

        const val SEARCH_FIELD_MASK =
            "places.id,places.displayName,places.location,places.formattedAddress"
    }
}
